import { useEffect, useState } from "react";
import {
  authDisabled,
  getWorkflowAccessToken,
  isAuthConfigured,
  loginRequest,
  msal,
  workflowEnabled,
} from "./auth";
import { getServiceAvailability } from "./workflow-api";

const portalUrl = String(import.meta.env.VITE_PORTAL_URL || "").trim();

function ServiceIcon() {
  return <div className="service-availability-icon" aria-hidden="true">
    <svg viewBox="0 0 48 48" role="presentation">
      <rect x="8.5" y="11.5" width="31" height="25" rx="6" />
      <circle cx="24" cy="22" r="5.5" />
      <path d="M15.5 32c2.3-4.2 5.1-6.2 8.5-6.2s6.2 2 8.5 6.2" />
      <path d="M34 15.5h2.5" />
    </svg>
  </div>;
}

function AvailabilityScreen({ mode, companyName, onRetry, onSignOut }) {
  const unavailable = mode === "unavailable";
  return <main className="service-availability-shell">
    <section className="service-availability-card" aria-live="polite">
      <img className="service-availability-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
      <ServiceIcon />
      <p className="service-availability-kicker">Profile Picture Studio</p>
      <h1>{unavailable
        ? "This service is not available for your Business Unit yet"
        : "We couldn’t confirm service availability"}</h1>
      <p className="service-availability-copy">{unavailable
        ? "Profile Picture Studio has not yet been activated for your Business Unit."
        : "We couldn’t verify your Business Unit access right now. Please try again before entering the Studio."}</p>
      {unavailable && companyName && <div className="service-availability-bu">
        <span>Business Unit</span>
        <strong>{companyName}</strong>
      </div>}
      {unavailable
        ? <p className="service-availability-help">If you believe you should have access, please contact your <strong>HC representative</strong> or <strong>IT Support</strong>.</p>
        : <p className="service-availability-help">Please try again in a moment. If the problem continues, contact <strong>IT Support</strong>.</p>}
      <div className="service-availability-actions">
        {unavailable && portalUrl
          ? <a className="service-availability-primary" href={portalUrl}>Return to Portal</a>
          : !unavailable && <button className="service-availability-primary" type="button" onClick={onRetry}>Try again</button>}
        {onSignOut && <button className={unavailable && !portalUrl ? "service-availability-primary" : "service-availability-secondary"} type="button" onClick={onSignOut}>Sign out</button>}
      </div>
    </section>
  </main>;
}

function CheckingScreen({ title = "Checking service availability…" }) {
  return <main className="service-availability-shell">
    <section className="service-availability-card service-availability-checking" aria-live="polite">
      <img className="service-availability-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
      <div className="service-availability-spinner" aria-hidden="true" />
      <p className="service-availability-kicker">Profile Picture Studio</p>
      <h1>{title}</h1>
      <p className="service-availability-copy">We’re confirming that Profile Picture Studio is active for your Business Unit.</p>
    </section>
  </main>;
}

function SignInScreen({ error, onSignIn }) {
  return <main className="service-availability-shell">
    <section className="service-availability-card">
      <img className="service-availability-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
      <ServiceIcon />
      <p className="service-availability-kicker">Profile Picture Studio</p>
      <h1>Employee sign in</h1>
      <p className="service-availability-copy">Use your Rawabi Microsoft account to continue.</p>
      <div className="service-availability-actions">
        <button className="service-availability-primary" type="button" onClick={onSignIn}>Sign in with Microsoft</button>
      </div>
      {error && <p className="service-availability-inline-error" role="alert">{error}</p>}
    </section>
  </main>;
}

export default function ServiceAvailabilityGate({ children }) {
  const enabled = !authDisabled && workflowEnabled && isAuthConfigured && Boolean(msal);
  const [status, setStatus] = useState(enabled ? "loading" : "ready");
  const [account, setAccount] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState("");
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;

    (async () => {
      setError("");
      setStatus("loading");
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const signedIn = redirect?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
        if (!active) return;
        if (!signedIn) {
          setAccount(null);
          setStatus("anonymous");
          return;
        }

        msal.setActiveAccount(signedIn);
        setAccount(signedIn);
        const result = await getServiceAvailability(() => getWorkflowAccessToken(signedIn));
        if (!active) return;
        const serviceAvailability = result?.serviceAvailability || null;
        setAvailability(serviceAvailability);
        setStatus(serviceAvailability?.available === true ? "ready" : "unavailable");
      } catch (checkError) {
        if (!active) return;
        setError(checkError instanceof Error ? checkError.message : String(checkError));
        setStatus("error");
      }
    })();

    return () => { active = false; };
  }, [enabled, retryVersion]);

  async function signIn() {
    setError("");
    try {
      await msal.loginRedirect(loginRequest);
    } catch (signInError) {
      setError(signInError instanceof Error ? signInError.message : String(signInError));
    }
  }

  async function signOut() {
    await msal.logoutRedirect({ account: account || undefined });
  }

  if (status === "ready") return children;
  if (status === "loading") return <CheckingScreen />;
  if (status === "anonymous") return <SignInScreen error={error} onSignIn={signIn} />;
  if (status === "unavailable") {
    return <AvailabilityScreen mode="unavailable" companyName={availability?.companyName} onSignOut={signOut} />;
  }
  return <AvailabilityScreen mode="error" onRetry={() => setRetryVersion((value) => value + 1)} onSignOut={account ? signOut : null} />;
}
